import classNames from 'classnames';
import { useSelector } from 'react-redux';
import type { MouseEvent, VoidFunctionComponent } from 'react';

import { renderRevision } from '../../../../../shared/helpers/renderLocator';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { isFullHash } from '../../../../utils';
import { NEW_TAB_PROPS } from '../../../generic/Link/Link';
import Tooltip from '../../../generic/Tooltip';

type IssueTitleProps = {
  title?: string;
  packageManager: string;
  dependency: string;
  version: string | null;
  //  This is so we can allow right/middle-clicking to "open in new tab"
  issueDetailsURL: string;
};

/**
 * Displays the title of the issue. It acts as a link in cases
 * where the user is right clicking/opening in a new tab, where
 * the full-page issue details is displayed. Otherwise, in cases
 * where it's just clicked regularly, it allows the event to
 * bubble up to the parent (BaseIssueItem), which will open the issue drawer.
 */
const IssueTitle: VoidFunctionComponent<IssueTitleProps> = ({
  title,
  packageManager,
  dependency,
  version,
  issueDetailsURL,
}) => {
  const grouping = useSelector(IssuesV2Store.selectors.getGrouping);
  const revisionClassName = classNames({ 'issue-item__dependency-revision--truncated': isFullHash(version ?? '') });
  const locator = `${packageManager}+${dependency}$${version}`;
  const revision = renderRevision(locator);

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const isCMDClick = event.metaKey === true || event.ctrlKey === true;
    //  Handle regular clicks, which should bubble up to the parent.
    //  For this, we prevent the default browser behavior (which is for this
    //  link to be followed).
    if (!isCMDClick && event.nativeEvent.type === 'click') {
      event.preventDefault();
    }
  };

  return (
    <a
      href={issueDetailsURL}
      type="button"
      className="issue-item__title--container issue-item__title--container--button"
      onClick={onClick}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...NEW_TAB_PROPS}
    >
      <span className="issue-item__title">{title}</span>
      {grouping === 'issue' &&
        <>
          <span className="issue-item__dependency"> in {dependency}</span>
          {version &&
            <Tooltip id={`issue-item-version-${locator}`} tooltip={revision}>
              <span className="issue-item__dependency-revision">
                &nbsp;(<span className={revisionClassName}>{revision}</span>)
              </span>
            </Tooltip>}
        </>}
    </a>
  );
};

export default IssueTitle;
